import React from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IGroupedMaterialTransaction, IRawMaterial } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { formatCurrency, formatDateTime, formatDecimal } from '../utils/format';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { RefresherEvent } from '../types/ionic';

interface Params {
  id: string;
}

const MaterialDetail: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';

  const materialsQuery = useQuery({
    queryKey: ['materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRawMaterial[]>('/materials');
      return data;
    },
  });

  const material = materialsQuery.data?.find((item) => item.id === id) || null;

  const transactionsQuery = useQuery({
    queryKey: ['material-transactions', id],
    queryFn: async () => {
      const { data } = await apiClient.get<IGroupedMaterialTransaction[]>(
        `/materials/${id}/transactions/grouped`,
      );
      return data;
    },
    enabled: !!id,
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await Promise.all([materialsQuery.refetch(), transactionsQuery.refetch()]);
    } finally {
      event.detail.complete();
    }
  };

  if (!id) {
    history.replace('/tabs/materials');
    return null;
  }

  const formatQuantity = (value: number, unit: string) =>
    `${formatDecimal(Number(value || 0), {
      locale,
      maximumFractionDigits: 6,
    })} ${unit}`;

  const formatVND = (value: number) => formatCurrency(Number(value || 0), locale);

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/materials" />
          </IonButtons>
          <IonTitle>{material?.name || t('materials')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          {materialsQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : !material ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <>
              <section style={{ marginBottom: 24 }}>
                <h2 style={{ marginBottom: 8 }}>{material.name}</h2>
                <IonButton
                  expand="block"
                  routerLink={`/tabs/materials/${id}/import`}
                  color="primary"
                  style={{ marginBottom: 16 }}
                >
                  {t('addImport') ?? 'Add Import'}
                </IonButton>
                <IonList>
                  <IonItem lines="none">
                    <IonLabel>
                      <p>{t('currentStock')}</p>
                      <h3>{formatQuantity(Number(material.currentStock || 0), material.unit)}</h3>
                    </IonLabel>
                  </IonItem>
                  <IonItem lines="none">
                    <IonLabel>
                      <p>{t('minStockLevel')}</p>
                      <h3>{formatQuantity(Number(material.minStockLevel || 0), material.unit)}</h3>
                    </IonLabel>
                  </IonItem>
                  <IonItem lines="none">
                    <IonLabel>
                      <p>{t('costPerUnit')}</p>
                      <h3>{formatVND(Number(material.costPerUnit || 0))}</h3>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </section>

              <section>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('importHistory')}</h2>
                </IonText>
                {transactionsQuery.isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                    <IonSpinner />
                  </div>
                ) : transactionsQuery.data && transactionsQuery.data.length > 0 ? (
                  <IonList>
                    {transactionsQuery.data.map((group) => (
                      <IonItem key={group.groupId}>
                        <IonLabel>
                          <h3>{formatDateTime(group.date, 'dd MMM yyyy')}</h3>
                          <p>
                            {t('quantity')}: {formatQuantity(group.totalQuantity, material.unit)}
                          </p>
                          <p>
                            {t('costPerUnit')}: {formatVND(group.costPerUnit)} • {t('totalPrice')}:{' '}
                            {formatVND(group.totalPrice)}
                          </p>
                          {group.warehouseDistribution?.length ? (
                            <p>
                              {t('warehouse')}:{' '}
                              {group.warehouseDistribution
                                .map(
                                  (entry) =>
                                    `${entry.warehouseName}: ${formatQuantity(
                                      entry.quantity,
                                      material.unit,
                                    )}`,
                                )
                                .join('; ')}
                            </p>
                          ) : null}
                          {group.notes ? (
                            <p>
                              {t('notes')}: {group.notes}
                            </p>
                          ) : null}
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                ) : (
                  <IonText color="medium">
                    <p>{t('noHistory')}</p>
                  </IonText>
                )}
              </section>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MaterialDetail;
